// src/views/pages/Home/Home.jsx
import React, { useState, useEffect, memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle, Users, Building, MapPin, MessageCircle, Home as HomeIcon,
  Key, Briefcase, Map, ShieldCheck, ChevronDown, Search, Eye,
  ThumbsUp, Landmark, Compass, Paintbrush, Truck, TrendingUp
} from 'lucide-react';
import Button from '../../components/Button/Button';
import TrustBadge from '../../components/TrustBadge/TrustBadge';
import PropertyCard from '../../components/PropertyCard/PropertyCard';
import PromotionSection from '../../components/PromotionSection/PromotionSection';
import ScrollReveal from '../../components/Motion/ScrollReveal';
import SplitText from '../../components/Motion/SplitText';
import TiltCard from '../../components/Motion/TiltCard';
import { useLeadContext } from '../../../controllers/context/LeadContext';
import { LEAD_SOURCES, LEAD_TYPES } from '../../../models/leadTypes';
import { generateWhatsAppLink } from '../../../controllers/services/leadService';
import { supabase } from '../../../controllers/services/supabase';
import { mapDbPropertyToUi } from '../../../controllers/services/dbMapping';
import { SERVICE_AREA_PINCODES } from '../../../controllers/utils/locationValidator';
import { getSystemSetting } from '../../../controllers/services/systemSettingsService';
import { useSiteSettings } from '../../../controllers/context/SiteSettingsContext';
import './Home.css';

const IconMap = {
  Home: HomeIcon, Key: Key, Briefcase: Briefcase, Users: Users, Map: Map, Building: Building, ShieldCheck: ShieldCheck,
  Landmark: Landmark, Compass: Compass, Paintbrush: Paintbrush, Truck: Truck
};

const FAQItem = memo(({ faq }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="faq-item">
      <button className="faq-question" onClick={() => setIsOpen((prev) => !prev)}>
        <span>{faq.question}</span>
        <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.3 }}>
          <ChevronDown size={20} />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 350, damping: 25 }}
            className="faq-answer"
          >
            {faq.answer}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

const EMICalculator = memo(() => {
  const { whatsappPhone } = useSiteSettings();
  const [principal, setPrincipal] = useState(5000000);
  const [rate, setRate] = useState(8.5);
  const [tenure, setTenure] = useState(20);

  const calculateEMI = () => {
    const p = principal;
    const r = rate / 12 / 100;
    const n = tenure * 12;
    if (r === 0) return p / n;
    const emi = (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    return Math.round(emi);
  };

  const currentEmi = calculateEMI();

  return (
    <TiltCard maxTilt={5} className="calculator-card">
      <h3 className="text-2xl font-bold mb-6 text-center">Home Loan EMI Calculator</h3>
      <div className="calc-range-group">
        <div className="calc-range-label">
          <span>Loan Amount</span>
          <span className="font-bold text-accent">₹{(principal / 100000).toFixed(1)} Lacs</span>
        </div>
        <input
          type="range"
          min="1000000"
          max="50000000"
          step="500000"
          value={principal}
          onChange={(e) => setPrincipal(Number(e.target.value))}
          className="calc-range-input"
        />
      </div>
      <div className="calc-range-group">
        <div className="calc-range-label">
          <span>Interest Rate</span>
          <span className="font-bold text-accent">{rate}% p.a.</span>
        </div>
        <input
          type="range"
          min="5"
          max="15"
          step="0.1"
          value={rate}
          onChange={(e) => setRate(Number(e.target.value))}
          className="calc-range-input"
        />
      </div>
      <div className="calc-range-group">
        <div className="calc-range-label">
          <span>Tenure</span>
          <span className="font-bold text-accent">{tenure} Years</span>
        </div>
        <input
          type="range"
          min="5"
          max="30"
          step="1"
          value={tenure}
          onChange={(e) => setTenure(Number(e.target.value))}
          className="calc-range-input"
        />
      </div>
      <div className="calc-result">
        <p>Your Estimated EMI</p>
        <motion.div
          key={currentEmi}
          initial={{ scale: 1.05 }}
          animate={{ scale: 1 }}
          className="calc-emi-amount"
        >
          ₹{currentEmi.toLocaleString('en-IN')} / mo
        </motion.div>
        <Button
          variant="accent"
          magnetic
          style={{ marginTop: '1rem' }}
          onClick={() =>
            window.open(
              generateWhatsAppLink('Hi, I want to apply for a home loan.', whatsappPhone),
              '_blank'
            )
          }
        >
          Apply for Loan
        </Button>
      </div>
    </TiltCard>
  );
});

const Home = () => {
  const { openPopup } = useLeadContext();
  const { whatsappPhone } = useSiteSettings();
  const navigate = useNavigate();

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([
    { id: 'c1', title: 'Buy Property', count: '0', icon: 'Home' },
    { id: 'c2', title: 'Rent Property', count: '0', icon: 'Key' },
    { id: 'c3', title: 'Commercial', count: '0', icon: 'Briefcase' },
    { id: 'c4', title: 'PG/Co-living', count: '0', icon: 'Users' },
    { id: 'c5', title: 'Plots/Land', count: '0', icon: 'Map' },
    { id: 'c6', title: 'Builder Projects', count: '0', icon: 'Building' }
  ]);
  const [builderProjects, setBuilderProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [alliedServices, setAlliedServices] = useState([]);
  const [siteStats, setSiteStats] = useState({
    cities_count: 'Serving 3 Cities',
    consultants_count: '100+ Property Consultant',
    sellers_count: 'Trusted 100+ Seller',
    buyers_count: 'Active Buyer - 1000+'
  });

  useEffect(() => {
    const fetchSystemStats = async () => {
      const stats = await getSystemSetting('site_stats', null);
      if (stats) setSiteStats(stats);
    };
    fetchSystemStats();

    const fetchHomeProperties = async () => {
      try {
        const { data, error } = await supabase
          .from('properties')
          .select('*, users(full_name)')
          .eq('status', 'approved').in('pincode', SERVICE_AREA_PINCODES)
          .order('is_featured', { ascending: false })
          .order('approved_at', { ascending: false, nullsFirst: false })
          .limit(4);

        if (error) {
          console.error('Error fetching home properties:', error);
        } else {
          setProperties((data || []).map(mapDbPropertyToUi));
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    const fetchDynamicContent = async () => {
      try {
        const [
          { count: buyCount },
          { count: rentCount },
          { count: commercialCount },
          { count: pgCount },
          { count: plotsCount }
        ] = await Promise.all([
          supabase.from('properties').select('*', { count: 'exact', head: true }).eq('is_for_sale', true).eq('status', 'approved').in('pincode', SERVICE_AREA_PINCODES),
          supabase.from('properties').select('*', { count: 'exact', head: true }).eq('is_for_sale', false).not('property_type', 'ilike', '%pg%').eq('status', 'approved').in('pincode', SERVICE_AREA_PINCODES),
          supabase.from('properties').select('*', { count: 'exact', head: true }).or('property_type.ilike.%commercial%,property_type.ilike.%warehouse%,property_type.ilike.%office%').eq('status', 'approved').in('pincode', SERVICE_AREA_PINCODES),
          supabase.from('properties').select('*', { count: 'exact', head: true }).ilike('property_type', '%pg%').eq('status', 'approved').in('pincode', SERVICE_AREA_PINCODES),
          supabase.from('properties').select('*', { count: 'exact', head: true }).or('property_type.ilike.%plot%,property_type.ilike.%land%').eq('status', 'approved').in('pincode', SERVICE_AREA_PINCODES)
        ]);

        const { count: buildersCount } = await supabase.from('builder_projects').select('*', { count: 'exact', head: true });

        setCategories([
          { id: 'c1', title: 'Buy Property', count: `${buyCount || 0}+`, icon: 'Home' },
          { id: 'c2', title: 'Rent Property', count: `${rentCount || 0}+`, icon: 'Key' },
          { id: 'c3', title: 'Commercial', count: `${commercialCount || 0}+`, icon: 'Briefcase' },
          { id: 'c4', title: 'PG/Co-living', count: `${pgCount || 0}+`, icon: 'Users' },
          { id: 'c5', title: 'Plots/Land', count: `${plotsCount || 0}+`, icon: 'Map' },
          { id: 'c6', title: 'Builder Projects', count: `${buildersCount || 0}+`, icon: 'Building' }
        ]);

        const { data: builderData } = await supabase.from('builder_projects').select('*').order('created_at', { ascending: true }).limit(3);
        if (builderData) {
          setBuilderProjects(builderData.map(p => ({
            id: p.id, projectName: p.project_name, builder: p.builder, location: p.location, startingPrice: p.starting_price, image: p.image_url
          })));
        }

        const { data: testimonialData } = await supabase.from('testimonials').select('*').limit(3);
        if (testimonialData) setTestimonials(testimonialData);

        const { data: faqData } = await supabase.from('faqs').select('*');
        if (faqData) setFaqs(faqData);

        let neighborhoodList = [];
        try {
          const { data: neighborhoodData } = await supabase
            .from('neighborhoods')
            .select('*')
            .eq('status', 'Active')
            .order('priority', { ascending: true });

          if (neighborhoodData && neighborhoodData.length > 0) {
            neighborhoodList = neighborhoodData;
          }
        } catch (nErr) {
          console.warn('Could not query neighborhoods from Supabase:', nErr);
        }

        // Resilient Fallback to LocalStorage or Default Authentic Locations
        if (neighborhoodList.length === 0) {
          try {
            const rawFallback = localStorage.getItem('nobrokerage_neighborhoods_fallback');
            if (rawFallback) {
              const parsed = JSON.parse(rawFallback).filter(n => n.status === 'Active');
              if (parsed.length > 0) neighborhoodList = parsed;
            }
          } catch (e) {}
        }

        if (neighborhoodList.length === 0) {
          neighborhoodList = [
            {
              id: 'bbc09e52-e192-4a44-81b8-85f136a22721',
              name: 'Assi Ghat',
              city: 'Varanasi, UP',
              price_trend: '+15.1%',
              source_note: 'Indicative (Circle Rate Trend)',
              image_url: '/images/neighborhoods/assi-ghat.jpg',
              priority: 1
            },
            {
              id: '8b8ea352-ffc7-4bfb-bb75-9d4f1b309f05',
              name: 'Gomti Nagar',
              city: 'Lucknow, UP',
              price_trend: '+12.5%',
              source_note: 'Indicative (Market Survey)',
              image_url: '/images/neighborhoods/gomti-nagar.jpg',
              priority: 2
            },
            {
              id: '86a986a4-7110-4df1-a610-619bb6891d76',
              name: 'Mahua Bagh',
              city: 'Ghazipur, UP',
              price_trend: '+4.8%',
              source_note: 'Indicative (Registry Index)',
              image_url: '/images/neighborhoods/mahua-bagh.jpg',
              priority: 3
            }
          ];
        }

        setNeighborhoods(neighborhoodList.map(n => ({
          id: n.id,
          name: n.name,
          city: n.city,
          priceTrend: n.price_trend,
          sourceNote: n.source_note || 'Indicative',
          image: n.image_url
        })));

        const { data: alliedData } = await supabase.from('allied_services').select('*');
        if (alliedData) setAlliedServices(alliedData);
      } catch (err) {
        console.error('Error fetching dynamic content for home page:', err);
      }
    };

    fetchHomeProperties();
    fetchDynamicContent();
  }, []);

  const handleOpenVerifiedListPopup = useCallback(() => {
    openPopup('Get Verified Property List', LEAD_SOURCES.HOME_PAGE_AUTO, LEAD_TYPES.BUYER);
  }, [openPopup]);

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section" style={{ position: 'relative', overflow: 'hidden' }}>
        <motion.div
          className="hero-bg"
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }}
        />
        <div className="hero-overlay"></div>
        <div className="container hero-content">
          <SplitText
            text="Buy • Sell • Rent • Build"
            className="hero-title"
            as="h1"
            delay={0.1}
          />
          <h1 className="hero-title" style={{ marginTop: '-0.5rem' }}>
            <span className="gradient-text">Without Brokerage</span>
          </h1>

          <motion.p
            className="hero-subtitle"
            initial={{ opacity: 0, y: 20, filter: 'blur(4px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Find verified properties, trusted builders, construction services, and local opportunities without paying brokerage.
          </motion.p>

          <motion.div
            className="hero-actions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Button
              variant="accent"
              icon={MessageCircle}
              magnetic
              onClick={() => window.open(generateWhatsAppLink('Hi NOBROKERAGE, I am looking for properties.', whatsappPhone), '_blank')}
            >
              Chat on WhatsApp
            </Button>
            <Button
              variant="outline"
              magnetic
              style={{ borderColor: 'white', color: 'white' }}
              onClick={handleOpenVerifiedListPopup}
            >
              Get Verified Property List
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="trust-section">
        <div className="container">
          <ScrollReveal direction="up" distance={40}>
            <div className="trust-grid">
              <TrustBadge icon={MapPin} title={siteStats.cities_count || 'Serving 3 Cities'} description="Hyperlocal Coverage" />
              <TrustBadge icon={Users} title={siteStats.consultants_count || '100+ Property Consultant'} description="Verified Partners" />
              <TrustBadge icon={CheckCircle} title={siteStats.sellers_count || 'Trusted 100+ Seller'} description="Direct Owners" />
              <TrustBadge icon={Building} title={siteStats.buyers_count || 'Active Buyer - 1000+'} description="Active Community" />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Categories Section */}
      <section className="section-padding">
        <div className="container">
          <ScrollReveal direction="up" distance={30}>
            <div className="section-header">
              <h2>Explore Real Estate Options</h2>
              <p>Find exactly what you are looking for</p>
            </div>
          </ScrollReveal>

          <div className="categories-grid">
            {categories.map((cat, index) => {
              const Icon = IconMap[cat.icon];
              return (
                <ScrollReveal key={cat.id} direction="up" delay={index * 0.08} scale>
                  <motion.div
                    className="category-card glass"
                    onClick={() => navigate(cat.title.includes('Rent') ? '/rent' : '/buy')}
                    whileHover={{ y: -8, scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                  >
                    <motion.div
                      className="category-icon"
                      whileHover={{ rotate: 12, scale: 1.1 }}
                    >
                      <Icon size={32} />
                    </motion.div>
                    <h4 className="category-title">{cat.title}</h4>
                    <p className="category-count">{cat.count} listings</p>
                  </motion.div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Top Investment Neighborhoods */}
      <section className="section-padding neighborhoods-section">
        <div className="container">
          <ScrollReveal direction="up">
            <div className="section-header neighborhoods-header">
              <h2>Top Investment Neighborhoods</h2>
              <p>Discover high-growth localities with the best verified property deals and zero brokerage</p>
            </div>
          </ScrollReveal>

          <div className="neighborhoods-grid">
            {neighborhoods.map((hood, index) => {
              const cleanCity = (hood.city || '').split(',')[0].trim();
              return (
                <ScrollReveal key={hood.id || index} direction="up" delay={index * 0.1}>
                  <TiltCard
                    maxTilt={8}
                    className="neighborhood-card"
                    onClick={() => navigate(`/buy?city=${encodeURIComponent(cleanCity)}&locality=${encodeURIComponent(hood.name)}`)}
                    role="button"
                    tabIndex={0}
                    aria-label={`Explore properties in ${hood.name}, ${hood.city}`}
                  >
                    <img
                      src={hood.image}
                      alt={`${hood.name}, ${hood.city}`}
                      width="380"
                      height="300"
                      loading="lazy"
                      decoding="async"
                      className="neighborhood-img"
                    />
                    <div className="neighborhood-overlay">
                      {hood.priceTrend && (
                        <div className="neighborhood-badge-wrap">
                          <span className="neighborhood-price-trend">
                            <TrendingUp size={12} />
                            <span>{hood.priceTrend} YOY</span>
                          </span>
                          {hood.sourceNote && (
                            <span className="neighborhood-source-note">{hood.sourceNote}</span>
                          )}
                        </div>
                      )}
                      <h3 className="neighborhood-title">{hood.name}</h3>
                      <p className="neighborhood-city">
                        <MapPin size={14} style={{ color: 'var(--brand-red, #A8080C)' }} />
                        <span>{hood.city}</span>
                      </p>
                    </div>
                  </TiltCard>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section className="section-padding">
        <div className="container">
          <ScrollReveal direction="up">
            <div className="section-header">
              <h2>Featured Properties</h2>
              <p>Handpicked premium properties with zero brokerage</p>
            </div>
          </ScrollReveal>

          <div className="properties-grid">
            {loading ? (
              [1, 2, 3, 4].map((n) => (
                <div key={n} className="skeleton-shimmer" style={{ height: '380px', borderRadius: 'var(--radius-xl)' }} />
              ))
            ) : properties.length > 0 ? (
              properties.map((property, idx) => (
                <ScrollReveal key={property.id} direction="up" delay={idx * 0.1}>
                  <PropertyCard property={property} />
                </ScrollReveal>
              ))
            ) : (
              <div style={{ textAlign: 'center', gridColumn: '1 / -1', padding: '2rem', color: 'var(--text-muted)' }}>
                No properties published yet.
              </div>
            )}
          </div>
          <div className="text-center" style={{ marginTop: '3rem' }}>
            <Button variant="outline" magnetic onClick={() => navigate('/buy')}>
              View All Properties
            </Button>
          </div>
        </div>
      </section>

      {/* Brand Promotion Videos Section */}
      <PromotionSection position="Promotion Section" />

      {/* How it Works Timeline */}
      <section className="section-padding" style={{ background: 'var(--primary)', color: 'white' }}>
        <div className="container">
          <ScrollReveal direction="up">
            <div className="section-header">
              <h2 style={{ color: 'white' }}>How NOBROKERAGE Works</h2>
              <p style={{ color: 'rgba(255,255,255,0.8)' }}>Your journey to a new home in 3 simple steps</p>
            </div>
          </ScrollReveal>

          <div className="timeline-container">
            {[
              { icon: Search, title: '1. Search & Shortlist', desc: 'Browse through thousands of verified properties and shortlist your favorites.' },
              { icon: Eye, title: '2. Free Site Visit', desc: 'Schedule a free site visit directly with the owner or builder.' },
              { icon: ThumbsUp, title: '3. Close the Deal', desc: 'Negotiate directly and close the deal. Pay absolutely zero brokerage.' }
            ].map((step, index) => (
              <ScrollReveal key={index} direction="left" delay={index * 0.15}>
                <div className="timeline-item">
                  <motion.div
                    className="timeline-icon"
                    whileHover={{ scale: 1.2, rotate: 10 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <step.icon size={32} />
                  </motion.div>
                  <div className="timeline-content text-main">
                    <h3 className="text-xl font-bold mb-2 text-primary">{step.title}</h3>
                    <p className="text-muted">{step.desc}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* EMI Calculator Section */}
      <section className="section-padding" style={{ background: 'var(--background)' }}>
        <div className="container">
          <ScrollReveal direction="up">
            <div className="section-header">
              <h2>Plan Your Finances</h2>
              <p>Calculate your EMI instantly</p>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="up" scale>
            <EMICalculator />
          </ScrollReveal>
        </div>
      </section>

      {/* Builder Projects */}
      <section className="section-padding">
        <div className="container">
          <ScrollReveal direction="up">
            <div className="section-header">
              <h2>Premium Builder Projects</h2>
              <p>Invest in top-tier upcoming developments</p>
            </div>
          </ScrollReveal>

          <div className="projects-grid">
            {builderProjects.map((project, index) => (
              <ScrollReveal key={project.id} direction="up" delay={index * 0.1}>
                <TiltCard maxTilt={8} className="project-card">
                  <motion.div
                    className="project-bg"
                    style={{ backgroundImage: `url(${project.image})` }}
                    whileHover={{ scale: 1.08 }}
                    transition={{ duration: 0.6 }}
                  />
                  <div className="project-overlay">
                    <h3 className="project-title">{project.projectName}</h3>
                    <div className="project-builder">By {project.builder}</div>
                    <div className="project-price">Starting from {project.startingPrice}</div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button
                        variant="primary"
                        style={{ flex: 1, padding: '0.5rem' }}
                        onClick={() => openPopup(`Enquire for ${project.projectName}`, LEAD_SOURCES.PROPERTY_ENQUIRY, LEAD_TYPES.BUILDER)}
                      >
                        Enquire Now
                      </Button>
                    </div>
                  </div>
                </TiltCard>
              </ScrollReveal>
            ))}
          </div>
          <div className="text-center" style={{ marginTop: '3rem' }}>
            <Button variant="outline" magnetic onClick={() => navigate('/builders')}>
              Explore All Projects
            </Button>
          </div>
        </div>
      </section>

      {/* Allied Services */}
      <section className="section-padding" style={{ background: 'var(--surface)' }}>
        <div className="container">
          <ScrollReveal direction="up">
            <div className="section-header">
              <h2>Our Allied Services</h2>
              <p>Everything you need under one roof</p>
            </div>
          </ScrollReveal>

          <div className="categories-grid">
            {alliedServices.map((service, index) => {
              const Icon = IconMap[service.icon];
              return (
                <ScrollReveal key={service.id} direction="up" delay={index * 0.08} scale>
                  <motion.div
                    className="category-card"
                    onClick={() => openPopup(`Enquire for ${service.title}`, LEAD_SOURCES.HOME_PAGE_AUTO, LEAD_TYPES.BUYER)}
                    whileHover={{ y: -6, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <motion.div className="category-icon" whileHover={{ rotate: 10 }}>
                      <Icon size={32} />
                    </motion.div>
                    <h4 className="category-title">{service.title}</h4>
                    <p className="category-count" style={{ marginTop: '0.5rem' }}>{service.description}</p>
                  </motion.div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Construction Services Preview */}
      <section className="section-padding" style={{ background: 'var(--background)' }}>
        <div className="container">
          <ScrollReveal direction="up">
            <div className="section-header">
              <h2>Construction & Renovation</h2>
              <p>Build your dream home with our verified contractors</p>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="up" scale>
            <div className="construction-banner">
              <div className="construction-content">
                <h3 style={{ fontSize: '2rem', marginBottom: '1rem', color: 'var(--text-main)' }}>End-to-End Construction Services</h3>
                <p style={{ marginBottom: '2rem', fontSize: '1.1rem' }}>
                  From architectural design to interior completion, our verified contractors ensure quality, transparency, and timely delivery.
                </p>
                <ul style={{ listStyle: 'none', marginBottom: '2rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <li style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}><CheckCircle size={18} className="text-success" /> Transparent Pricing</li>
                  <li style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}><CheckCircle size={18} className="text-success" /> Verified Contractors</li>
                  <li style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}><CheckCircle size={18} className="text-success" /> Quality Assurance</li>
                </ul>
                <Button variant="accent" magnetic onClick={() => openPopup('Get Construction Quote', LEAD_SOURCES.CONSTRUCTION_QUOTE, LEAD_TYPES.CONSTRUCTION)}>
                  Get Free Estimate
                </Button>
              </div>
              <div className="construction-image"></div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding">
        <div className="container">
          <ScrollReveal direction="up">
            <div className="section-header">
              <h2>What Our Users Say</h2>
              <p>Real stories from real users</p>
            </div>
          </ScrollReveal>

          <div className="testimonials-grid">
            {testimonials.map((t, index) => (
              <ScrollReveal key={t.id} direction="up" delay={index * 0.15}>
                <TiltCard maxTilt={6} className="testimonial-card">
                  <div style={{ color: '#FCD34D', marginBottom: '1rem' }}>★★★★★</div>
                  <p className="testimonial-text">"{t.text}"</p>
                  <div className="testimonial-author">
                    <div className="author-avatar">{t.name.charAt(0)}</div>
                    <div className="author-info">
                      <h4>{t.name}</h4>
                      <p>{t.role}</p>
                    </div>
                  </div>
                </TiltCard>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding" style={{ background: 'var(--background)' }}>
        <div className="container">
          <ScrollReveal direction="up">
            <div className="section-header">
              <h2>Frequently Asked Questions</h2>
              <p>Got questions? We have answers.</p>
            </div>
          </ScrollReveal>

          <div className="faq-container">
            {faqs.map((faq) => (
              <FAQItem key={faq.id} faq={faq} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
